import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import { useSelector } from "react-redux";
import CardHeader from "@commons/businesscardHeader";
import Fields from "@components/fields";
import Row from "@components/row";
import Wrapper from "@components/wrapper";
import Button from "@components/button";
import generalSettings from "@apis/generalSettings";
import { RootState } from "@store/index";
import { toast } from "react-toastify";

const LogoSetup = () => {
  const { data: logoSetup } = useSelector(
    (state: RootState) => state.logoSetup
  );

  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const fetchLogoSetup = async () => {
    await generalSettings.getByGroup({
      state: "logoSetup",
      id: "logosetup",
      showErr: false,
    });
  };

  useEffect(() => {
    fetchLogoSetup();
  }, []);

  // Map existing logos to initial values
  const initialValues = useMemo(() => {
    const values: Record<string, any> = {
      favicon: null,
      headerLogo: null,
      footerLogo: null,
    };
    if (!logoSetup) return values;

    logoSetup.forEach((item: any) => {
      if (item.key === "favicon") values.favicon = `${item.file}`;
      if (item.key === "header-logo") values.headerLogo = `${item.file}`;
      if (item.key === "footer-logo") values.footerLogo = `${item.file}`;
    });

    return values;
  }, [logoSetup]);

  const handleUpload = async (key: string, valueKey: string, file: any) => {
    if (!file) return alert("Please select a file first.");

    setLoadingKey(key); // Start loader for this button

    const formData = new FormData();
    formData.append("file", file.rawFile);
    formData.append("key", key);
    formData.append("group", "logosetup");
    formData.append("value", valueKey);

    try {
      const existing = logoSetup?.find((i: any) => i.key === key);
      if (existing) {
        await generalSettings.update({
          state: valueKey,
          id: existing.id,
          data: formData,
        });
        toast.success(`${key.replace("-", " ")} updated successfully!`);
      } else {
        await generalSettings.create({ state: valueKey, data: formData });
        toast.success(`${key.replace("-", " ")} uploaded successfully!`);
      }
    } catch (err) {
      toast.error(`Failed to upload ${key.replace("-", " ")}.`);
      console.error(err);
    } finally {
      setLoadingKey(null); // Stop loader
      fetchLogoSetup();
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Row>
            {[
              {
                title: "Favicon",
                key: "favicon",
                valueKey: "favicon",
                height: 200,
              },
              {
                title: "Header Logo",
                key: "header-logo",
                valueKey: "headerLogo",
                height: 200,
              },
              {
                title: "Footer Logo",
                key: "footer-logo",
                valueKey: "footerLogo",
                height: 200,
              },
            ].map((item) => (
              <div key={item.key} style={{ flex: 1, width: "100%" }}>
                <CardHeader title={item.title} />
                <Wrapper>
                  <Fields.Upload
                    name={item.valueKey}
                    height={item.height}
                    useRawFile={true}
                    onChange={(file: any) => setFieldValue(item.valueKey, file)}
                  />
                  <div style={{ marginTop: "1rem", textAlign: "center" }}>
                    <Button
                      loading={loadingKey === item.key}
                      title={
                        values[item.valueKey]?.url
                          ? `Update ${item.title}`
                          : `Upload ${item.title}`
                      }
                      type="button"
                      onClick={() =>
                        handleUpload(
                          item.key,
                          item.valueKey,
                          values[item.valueKey]
                        )
                      }
                    />
                  </div>
                </Wrapper>
              </div>
            ))}
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default LogoSetup;
